import { FC } from 'react';

import { CampaignProductType } from 'utils/constants/campaign';

import { Avatar, Card, Flex, HSpacingContainer, Link, Text, TextTypography } from '@hh.ru/magritte-ui';
import { ChevronRightOutlinedSize16 } from '@hh.ru/magritte-ui/icon';
import { Link as SPALink } from '@hh.ru/redux-spa-middleware';

import { EMPTY_VALUE } from 'components/CampaignsDashboard/utils/constants';
import { getUrlToBanners } from 'components/CampaignsDashboard/utils/utils';

import { CampaignBanner } from 'store/models/campaignsDashboard';
import { useSelector } from 'store/utils';

interface CampaignBannersProps {
    banners: CampaignBanner[];
    campaignId: number;
    productType: CampaignProductType;
    isMobile?: boolean;
    typography?: TextTypography;
}

export const CampaignBanners: FC<CampaignBannersProps> = ({
    banners,
    campaignId,
    productType,
    isMobile = false,
    typography = 'label-3-regular',
}) => {
    const webdavPublicUrl = useSelector((state) => state.clickme.webdavPublicUrl);
    const previewSize = isMobile ? 24 : 40;

    if (banners.length === 0 || productType === CampaignProductType.SearchAuction) {
        return (
            <Text typography={typography} style="primary">
                {EMPTY_VALUE}
            </Text>
        );
    }

    const hasHiddenBanners = banners.length > 2;
    const visibleBanners = hasHiddenBanners ? banners.slice(0, 1) : banners;
    const bannerPreviews = visibleBanners.map(({ id, imageUrl }) => (
        <Avatar
            key={id}
            size={previewSize}
            mode="image"
            image={`${webdavPublicUrl}/${imageUrl}`}
            aria-label={imageUrl}
        />
    ));

    const previews = (
        <>
            {!isMobile && hasHiddenBanners ? (
                <Flex align="center" justify="center" height={previewSize} width={previewSize}>
                    {bannerPreviews}
                </Flex>
            ) : (
                bannerPreviews
            )}
            {hasHiddenBanners && (
                <Card
                    style="secondary"
                    padding={0}
                    borderRadius={isMobile ? 8 : 12}
                    borderWidth="default"
                    borderStyle="neutral"
                >
                    <Flex align="center" justify="center" height={previewSize} width={previewSize}>
                        <Text
                            style={isMobile ? 'primary' : 'secondary'}
                            typography={isMobile ? 'subtitle-4-semibold' : 'subtitle-1-semibold'}
                        >
                            {`+${banners.length - 1}`}
                        </Text>
                    </Flex>
                </Card>
            )}
        </>
    );

    if (isMobile) {
        return (
            <Flex
                Element={SPALink}
                to={getUrlToBanners(campaignId)}
                align="center"
                gap={4}
                height={24}
                width="max-content"
                ml="auto"
                style={{ lineHeight: 0, textDecoration: 'none' }}
                data-qa={`campaign-banners-link-${campaignId}`}
            >
                {previews}
                <ChevronRightOutlinedSize16 initialColor="secondary" />
            </Flex>
        );
    }

    return (
        <Link
            Element={SPALink}
            to={getUrlToBanners(campaignId)}
            mode="secondary"
            style="neutral"
            data-qa={`campaign-banners-link-${campaignId}`}
        >
            <div style={{ lineHeight: 0 }}>
                <HSpacingContainer default={4}>{previews}</HSpacingContainer>
            </div>
        </Link>
    );
};
